import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'

import { QueryClient, QueryClientProvider } from 'react-query'
import { QueryBoundaries } from 'components/QueryBoundaries'
import LoadingView from 'components/QueryBoundaries/LoadingView'

import InterWebviewBridge, {
  WbEnvironments,
  WbSessionActions,
  interWbNavigate,
  interWbHttp,
  interWbSession,
} from '@interco/inter-webview-bridge'
import { TrackData } from '@interco/track'
import { SnackBarProvider } from '@interco/inter-ui/components/SnackBar'

import axios from './axios'
import { TApplicationContext } from './types'

const initialApp: TApplicationContext = {
  platform: WbEnvironments.BROWSER,
  httpHandler: interWbHttp,
  showBottomSheet: false,
  setShowBottomSheet: () => null,
  userInfo: {
    account: '',
    userType: '',
    segmentation: '',
  },
}

const ApplicationContext = createContext<TApplicationContext>(initialApp)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      suspense: true,
      retry: 2,
      staleTime: Infinity,
    },
  },
})

export const useApplicationCtx = () => {
  const applicationCtx = useContext(ApplicationContext)

  if (!applicationCtx) {
    throw new Error('useApplicationCtx must be used within ApplicationProvider')
  }
  return applicationCtx
}

const iWb = InterWebviewBridge.getInstance()

const ApplicationProvider = ({ children, account }: { children: ReactNode; account?: string }) => {
  const [platform, setPlatform] = useState<WbEnvironments>(initialApp.platform)
  const [userInfo, setUserInfo] = useState(initialApp.userInfo)
  const [showBottomSheet, setShowBottomSheet] = useState(false)

  useEffect(() => {
    /**
     *@summary
     *Adiciona listener para back nativo
     */
    const reqGoBack = () => {
      interWbNavigate.requestGoBack()
    }
    iWb.addWebviewEventListener(WbSessionActions.GO_BACK, reqGoBack)
    return () => iWb.removeWebviewEventListener(WbSessionActions.GO_BACK, reqGoBack)
  }, [])

  const initOrUpdateNewRelic = (commonData: Record<string, unknown>) => {
    TrackData.initOrUpdate({
      runtimeEnv: process.env.REACT_APP_ENV as EnvType,
      projectName: 'TRANSACTION_FRONT',
      allowLocalHost: true,
      commonData: {
        projectName: process.env.REACT_APP_NPM_PACKAGE_NAME,
        ...commonData,
      },
    })
  }

  useEffect(() => {
    const envPlatform = iWb.getEnvironment()
    initApp(envPlatform)
  }, [])

  const initApp = async (envPlatform: WbEnvironments) => {
    if (['IOS', 'ANDROID'].includes(envPlatform)) {
      const sessionUserInfo = await interWbSession.getUserInfo()
      initOrUpdateNewRelic({
        account: sessionUserInfo.account,
        segmentation: sessionUserInfo.segmentation,
        userType: sessionUserInfo.userType,
        envPlatform,
      })
      setUserInfo(() => sessionUserInfo)
      setPlatform(envPlatform)
    } else {
      setUserInfo(() => ({
        account: account ?? '1234',
        userType: '',
        segmentation: '',
      }))
      initOrUpdateNewRelic({
        envPlatform,
      })
    }
  }

  const values = useMemo(
    () => ({
      ...initialApp,
      userInfo,
      showBottomSheet,
      setShowBottomSheet,
      httpHandler: ['IOS', 'ANDROID'].includes(platform) ? interWbHttp : axios,
      // httpHandler: axios,
    }),
    [platform, userInfo, setShowBottomSheet, showBottomSheet],
  )

  if (!userInfo.account) {
    return <LoadingView />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApplicationContext.Provider value={values}>
        <QueryBoundaries>
          <SnackBarProvider>{children}</SnackBarProvider>
        </QueryBoundaries>
      </ApplicationContext.Provider>
    </QueryClientProvider>
  )
}

export { ApplicationContext, ApplicationProvider }
